/**
 * Fetches what the dashboard needs, and reports honestly how much it got.
 *
 * THE SHAPE OF THE PROBLEM. There is no aggregate endpoint. `GET /api/jobs`
 * carries no candidate counts, so four of the five panels need
 * `GET /api/jobs/{id}/candidates` once per job. For a customer with fifty
 * jobs that is fifty-one requests on page load.
 *
 * Rather than pretend otherwise, this hook:
 *
 *   - bounds concurrency, so the browser is not handed fifty parallel requests
 *   - caps how many jobs it will fan out over, and SAYS SO when it caps
 *   - counts jobs whose candidates failed to load, and SAYS SO
 *   - reports partial results as they arrive rather than blocking on all of it
 *
 * The counting matters more than the bounding. A dashboard that silently
 * covers 25 of 50 jobs is worse than one that covers 25 and says so, because
 * the reader has no way to tell a small pipeline from a truncated view of a
 * large one.
 *
 * An aggregate endpoint would remove all of this. Until one exists, the panels
 * state their basis.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../../../axiosConfig';
import { CandidateRow, HiredDetail, JobRow } from '../utils/aggregate';

/** Jobs we will fan out over. Beyond this the dashboard reports a partial view. */
export const MAX_JOBS_FANNED_OUT = 25;

/** Simultaneous in-flight candidate requests. */
export const CONCURRENCY = 4;

/** Candidates requested per job. */
const CANDIDATE_PAGE_SIZE = 100;

/** HIRED candidates we will fetch detail for, to measure time-to-hire. */
export const MAX_HIRED_DETAIL = 40;

/** Candidates grouped by the job they were fetched for. */
export interface JobCandidates {
  jobId: number;
  rows: CandidateRow[];
}

export interface DashboardData {
  jobs: JobRow[];
  candidates: CandidateRow[];
  /**
   * The same candidates, still grouped by job.
   *
   * The flat `candidates` array cannot be regrouped after the fact:
   * `CandidateItem` carries `jobTitle` but NOT `jobId` (checked against
   * openapi.yaml, not assumed), and titles are not unique. The association
   * exists only here, at the point of the request that produced each batch,
   * so it is kept rather than rebuilt.
   *
   * Free to keep: the fan-out already runs one request per job, so per-job
   * aggregates cost no additional calls.
   */
  byJob: JobCandidates[];
  hired: HiredDetail[];
  /** Total jobs the API reports, which may exceed what we fanned out over. */
  totalJobs: number;
  /** Jobs we actually loaded candidates for. */
  jobsCovered: number;
  /** Jobs skipped because of MAX_JOBS_FANNED_OUT. */
  jobsSkipped: number;
  /** Jobs whose candidate request failed. Distinct from skipped. */
  jobsFailed: number;
  /** HIRED candidates whose detail we did not fetch, so time-to-hire is partial. */
  hiredDetailSkipped: number;
  loading: boolean;
  /** Set only when the initial jobs request fails -- nothing can be shown. */
  error: string | null;
}

const EMPTY: DashboardData = {
  jobs: [],
  candidates: [],
  byJob: [],
  hired: [],
  totalJobs: 0,
  jobsCovered: 0,
  jobsSkipped: 0,
  jobsFailed: 0,
  hiredDetailSkipped: 0,
  loading: true,
  error: null,
};

/** Runs tasks with a fixed number in flight, preserving nothing about order. */
const withConcurrency = async <T>(
  tasks: (() => Promise<T>)[],
  limit: number,
): Promise<PromiseSettledResult<T>[]> => {
  const results: PromiseSettledResult<T>[] = new Array(tasks.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () =>
    (async () => {
      for (;;) {
        const i = next;
        next += 1;
        if (i >= tasks.length) return;
        try {
          results[i] = { status: 'fulfilled', value: await tasks[i]() };
        } catch (e) {
          results[i] = { status: 'rejected', reason: e };
        }
      }
    })(),
  );
  await Promise.all(workers);
  return results;
};

export const useDashboardData = (): DashboardData & { reload: () => void } => {
  const [data, setData] = useState<DashboardData>(EMPTY);
  const [nonce, setNonce] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setData({ ...EMPTY, loading: true });

      // ---- jobs: one request, and the only one we cannot proceed without
      let jobs: JobRow[] = [];
      let totalJobs = 0;
      try {
        const res = await axios.get(
          `/api/jobs?pageNumber=1&pageSize=${MAX_JOBS_FANNED_OUT}&sort=id&order=DESC`,
        );
        jobs = res.data?.content ?? res.data?.jobs ?? res.data ?? [];
        if (!Array.isArray(jobs)) jobs = [];
        totalJobs = res.data?.totalElements ?? jobs.length;
      } catch (e) {
        if (!cancelled && alive.current) {
          setData({
            ...EMPTY,
            loading: false,
            error: 'Could not load jobs, so nothing below could be calculated.',
          });
        }
        return;
      }

      const jobsSkipped = Math.max(0, totalJobs - jobs.length);

      if (!cancelled && alive.current) {
        // Show the job-level panel immediately; candidates take longer.
        setData((d) => ({ ...d, jobs, totalJobs, jobsSkipped, loading: true }));
      }

      // ---- candidates: one request per job, bounded
      const settled = await withConcurrency(
        jobs.map((j) => async () => {
          const res = await axios.get(
            `/api/jobs/${j.id}/candidates?pageNumber=1&pageSize=${CANDIDATE_PAGE_SIZE}`,
          );
          const rows: CandidateRow[] =
            res.data?.content ?? res.data?.candidates ?? res.data ?? [];
          return { jobId: j.id, rows: Array.isArray(rows) ? rows : [] };
        }),
        CONCURRENCY,
      );

      const candidates: CandidateRow[] = [];
      const byJob: JobCandidates[] = [];
      const hiredIds: { jobId: number; id: number }[] = [];
      let jobsFailed = 0;

      settled.forEach((r) => {
        if (r.status !== 'fulfilled') {
          jobsFailed += 1;
          return;
        }
        // A job whose request failed is absent from byJob rather than present
        // with zero rows -- "we could not load this" is not "this has none".
        byJob.push({ jobId: r.value.jobId, rows: r.value.rows });
        r.value.rows.forEach((row) => {
          candidates.push(row);
          if (row.status === 'HIRED') {
            hiredIds.push({ jobId: r.value.jobId, id: row.id });
          }
        });
      });

      if (!cancelled && alive.current) {
        setData((d) => ({
          ...d,
          jobs,
          totalJobs,
          jobsSkipped,
          jobsFailed,
          jobsCovered: jobs.length - jobsFailed,
          candidates,
          byJob,
          loading: true,
        }));
      }

      // ---- time to hire: statusChangedAt lives only on the DETAIL response,
      // so this is a per-candidate fetch. Affordable only because it is
      // restricted to HIRED, and capped even then.
      const toFetch = hiredIds.slice(0, MAX_HIRED_DETAIL);
      const hiredDetailSkipped = hiredIds.length - toFetch.length;

      const hiredSettled = await withConcurrency(
        toFetch.map((h) => async () => {
          const res = await axios.get(
            `/api/jobs/${h.jobId}/candidates/${h.id}`,
          );
          return {
            id: h.id,
            jobId: h.jobId,
            statusChangedAt: res.data?.statusChangedAt,
          } as HiredDetail;
        }),
        CONCURRENCY,
      );

      const hired = hiredSettled
        .filter(
          (r): r is PromiseFulfilledResult<HiredDetail> =>
            r.status === 'fulfilled',
        )
        .map((r) => r.value);

      if (!cancelled && alive.current) {
        setData({
          jobs,
          candidates,
          byJob,
          hired,
          totalJobs,
          jobsCovered: jobs.length - jobsFailed,
          jobsSkipped,
          jobsFailed,
          hiredDetailSkipped,
          loading: false,
          error: null,
        });
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  return { ...data, reload };
};
